/**
 * Pure TEA update for notes/database create dialogs.
 *
 * Chapter create stays in OutlineTree (folder dedup + drag handoff).
 */
import { assertNever } from "../lib/assertNever";
import type { UpdateFn } from "../lib/tea";

export type CreateEntityModel = {
  readonly open: boolean;
  readonly draft: string;
  readonly error: string;
};

export const initialCreateEntityModel: CreateEntityModel = {
  open: false,
  draft: "",
  error: "",
};

export type CreateEntityMsg =
  | { readonly type: "Start" }
  | { readonly type: "Cancel" }
  | { readonly type: "DraftChanged"; readonly draft: string }
  | { readonly type: "Commit" };

export type CreateEntityCmd =
  | { readonly kind: "focusInput" }
  | { readonly kind: "create"; readonly name: string };

export type CreateEntityDeps = {
  readonly existingNames: () => readonly string[];
  readonly normalizeName: (raw: string) => string;
  readonly emptyNameError: string;
  readonly duplicateError: string;
};

const closed: CreateEntityModel = initialCreateEntityModel;

function withError(
  model: CreateEntityModel,
  error: string,
): readonly [CreateEntityModel, readonly CreateEntityCmd[]] {
  return [{ ...model, error }, []];
}

export function updateCreateEntity(
  model: CreateEntityModel,
  msg: CreateEntityMsg,
  deps: CreateEntityDeps,
): readonly [CreateEntityModel, readonly CreateEntityCmd[]] {
  switch (msg.type) {
    case "Start":
      return [{ open: true, draft: "", error: "" }, [{ kind: "focusInput" }]];
    case "Cancel":
      return [closed, []];
    case "DraftChanged":
      return [{ ...model, draft: msg.draft, error: "" }, []];
    case "Commit": {
      const raw = model.draft.trim();
      if (raw.length === 0) {
        return withError(model, deps.emptyNameError);
      }
      if (raw.includes("/") || raw.includes("\\")) {
        return withError(model, "Names can’t include slashes.");
      }
      const name = deps.normalizeName(raw);
      const duplicate = deps
        .existingNames()
        .some((existing) => existing.toLowerCase() === name.toLowerCase());
      if (duplicate) {
        return withError(model, deps.duplicateError);
      }
      return [closed, [{ kind: "create", name }]];
    }
    default:
      return assertNever(msg);
  }
}

export function makeCreateEntityUpdate(
  deps: CreateEntityDeps,
): UpdateFn<CreateEntityModel, CreateEntityMsg, CreateEntityCmd> {
  return (model, msg) => updateCreateEntity(model, msg, deps);
}
